import { useMemo, useState } from "react";
import { Box, Button, IconButton } from "@mui/material";

import { CellContext } from "@tanstack/react-table";
import { Trash2Icon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { RulesPermissionEnum } from "authorization/types";
import { DataTable } from "components/DataTable/DataTable";
import { useAuthenticatedUser } from "hooks/useAuthenticatedUser";
import { useNavigate } from "hooks/useNavigate";
import { IRule } from "interfaces/rules.interface";
import { IZone } from "interfaces/zone.interface";

import {
  ActiveTimeFrameChip,
  InheritedChip,
  MemoizedZoneComponentComponent,
  PresetChip,
  RuleTypeChip,
} from "./RulesTableRowComponents";

import { NotificationEnableDisableButton } from "../../../../components/NotificationEnableDisableButton";
import { DeleteRuleDialog } from "../dialogs/DeleteRuleDialog";
import { ToggleRuleDialog } from "../dialogs/ToggleRuleDialog";

type TypeRuleEntity = "subject" | "zone";

type RulesListProps = {
  rules: IRule[];
  viewRuleDetails: (id: string) => void;
  ruleEntityType: TypeRuleEntity;
  zoneId: string;
  zoneEntity: { [key: string]: IZone };
};

export function RulesList({
  rules,
  viewRuleDetails,
  ruleEntityType,
  zoneId,
  zoneEntity,
}: RulesListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { permissions } = useAuthenticatedUser();
  const [confirmToggle, setConfirmToggle] = useState<string | null>(null);
  const [deleteRule, setDeleteRule] = useState<string | null>(null);

  const columns = useMemo(
    () => [
      {
        accessorKey: "type",
        header: t("Type"),
        cell: RuleTypeChip,
      },
      {
        accessorKey: "conditions_preset",
        header: t("preset"),
        cell: PresetChip,
      },
      {
        accessorKey: "active_timeframe",
        header: t("active_timeframe"),
        cell: ActiveTimeFrameChip,
      },
      {
        accessorKey: "inherited",
        header: t("inherited_direct"),
        cell: InheritedChip,
      },
      {
        accessorKey: "inherited_from",
        header: t("inherited_from"),
        cell: ({ row }: CellContext<IRule, unknown>) => {
          const zone = zoneEntity[row.original.zone_id ?? ""];

          if (!zone) return null;

          return (
            <MemoizedZoneComponentComponent
              zone={zone}
              selectedZone={zoneId}
              hasPermission={zone.hasAccess}
              onClick={(zone) => navigate(`/administration/zones/${zone.id}`)}
              ruleEntityType={ruleEntityType}
            />
          );
        },
      },
      {
        accessorKey: "enabled",
        header: t("enabled"),
        cell: ({ row }: CellContext<IRule, unknown>) => {
          const hasUpdateAccess = permissions.includes(
            RulesPermissionEnum["rule.create"],
          );
          const hasZoneAccess =
            zoneEntity[row.original.zone_id ?? ""]?.hasAccess ?? false;

          return (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
              }}
            >
              <NotificationEnableDisableButton
                enabled={!!row.original.enabled}
                inherited={!!row.original.inherited}
                type="rule"
                hasNoPermission={!hasUpdateAccess || !hasZoneAccess}
                callback={() => setConfirmToggle(row.original.id)}
              />

              <Button
                variant={"text"}
                onClick={() => viewRuleDetails(row.original.id)}
              >
                {t("details")}
              </Button>

              {(row.original.inherited || !hasUpdateAccess) && (
                <Box sx={{ visibility: "hidden", width: "32px" }} />
              )}

              {!row.original.inherited && hasUpdateAccess && (
                <IconButton
                  onClick={() => setDeleteRule(row.original.id)}
                  disabled={row.original.inherited ?? false}
                >
                  <Trash2Icon />
                </IconButton>
              )}
            </Box>
          );
        },
      },
    ],
    [
      navigate,
      permissions,
      ruleEntityType,
      t,
      viewRuleDetails,
      zoneEntity,
      zoneId,
    ],
  );

  const toggleRule = rules.find((r) => r.id === confirmToggle);

  return (
    <Box padding={1} width={"100%"}>
      <DataTable
        tableId={"rule-list"}
        columns={columns}
        data={rules}
        getRowId={(row) => row.id}
      />
      <ToggleRuleDialog
        rule={toggleRule}
        onClose={() => setConfirmToggle(null)}
      />
      <DeleteRuleDialog
        ruleId={deleteRule}
        onClose={() => setDeleteRule(null)}
      />
    </Box>
  );
}
